import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { notification } from "antd";
import axios from "axios";
import { useWatiSocket } from "./WatiSocketContext";
import { useCommonApiCall } from "../Api/commonApiCall";
import { useAuth } from "./AuthContext";
import watiNotificationSound from "../../assets/sounds/wati_notification.wav";
import whatsappIcon from "../../assets/images/whatsapp-96.png";

const CHANNEL_ID = "68d251023d3de3334068a1d9";
const SEND_ENDPOINT = "https://srm.uzdpart.com:33123/whatsapp/send_wati_general_message";

// Rendered inside Ant Design's notification portal — must not use any React context hooks.
// All data is passed as plain props captured at notification creation time.
const QuickReplyBox = ({ phone, userName, notificationKey, onNavigate, showCloseAll, onCloseAll }) => {
    const [text, setText] = React.useState("");
    const [sending, setSending] = React.useState(false);
    const [sent, setSent] = React.useState(false);

    const handleSend = async () => {
        if (!text.trim() || sending) return;
        setSending(true);
        try {
            await axios.post(SEND_ENDPOINT, {
                phone,
                message: text.trim(),
                user: userName,
                channelId: CHANNEL_ID,
            });
            setSent(true);
            setTimeout(() => notification.destroy(notificationKey), 800);
        } catch {
            setSending(false);
        }
    };

    return (
        <div onClick={e => e.stopPropagation()} style={{ marginTop: 8 }}>
            {sent ? (
                <div style={{ textAlign: "center", color: "#52c41a", fontWeight: 600, padding: "4px 0" }}>
                    ✓ Sent!
                </div>
            ) : (
                <>
                    <textarea
                        value={text}
                        onChange={e => setText(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                            }
                        }}
                        placeholder="Type a reply… (Enter to send)"
                        rows={2}
                        style={{
                            width: "100%",
                            resize: "none",
                            borderRadius: 6,
                            border: "1px solid #d9d9d9",
                            padding: "4px 8px",
                            fontSize: 12,
                            outline: "none",
                            boxSizing: "border-box",
                        }}
                    />
                    <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                        <button
                            onClick={handleSend}
                            disabled={!text.trim() || sending}
                            style={{
                                flex: 1,
                                backgroundColor: !text.trim() || sending ? "#d1d1d1" : "#075e54",
                                color: "white",
                                border: "none",
                                borderRadius: 6,
                                padding: "5px 8px",
                                cursor: !text.trim() || sending ? "not-allowed" : "pointer",
                                fontSize: 12,
                                fontWeight: 600,
                            }}
                        >
                            {sending ? "Sending…" : "Send ↵"}
                        </button>
                        <button
                            onClick={onNavigate}
                            style={{
                                backgroundColor: "transparent",
                                color: "#1677ff",
                                border: "1px solid #1677ff",
                                borderRadius: 6,
                                padding: "5px 10px",
                                cursor: "pointer",
                                fontSize: 11,
                                whiteSpace: "nowrap",
                            }}
                        >
                            Open chat →
                        </button>
                    </div>
                    {showCloseAll && (
                        <button
                            onClick={onCloseAll}
                            style={{
                                width: "100%",
                                marginTop: 4,
                                backgroundColor: "transparent",
                                color: "#888",
                                border: "1px solid #d9d9d9",
                                borderRadius: 6,
                                padding: "3px 8px",
                                cursor: "pointer",
                                fontSize: 11,
                            }}
                        >
                            Close all notifications
                        </button>
                    )}
                </>
            )}
        </div>
    );
};

const WatiChatContext = createContext();

export const useWatiChat = () => {
    const context = useContext(WatiChatContext);
    if (!context) {
        throw new Error("useWatiChat must be used within a WatiChatProvider");
    }
    return context;
};

export const WatiChatProvider = ({ children }) => {
    const { watiChatUpdates } = useWatiSocket();
    const { CommonApiCall } = useCommonApiCall();
    const { authState } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [chatlist, setChatlist] = useState([]);
    const [chatlistLoading, setChatlistLoading] = useState(true);
    const [continuationToken, setContinuationToken] = useState(null);

    const watiProcessedCountRef = useRef(0);
    // WhatsappMessages writes the currently open chat ID here so the
    // socket handler can keep unreadCount at 0 for that chat.
    const activeChatIdRef = useRef(null);
    const notificationAudioRef = useRef(new Audio(watiNotificationSound));
    const activeNotifsRef = useRef(new Set());

    const getAllChats = async () => {
        setChatlistLoading(true);
        try {
            const response = await CommonApiCall(
                { api_key: "uzdpart" },
                "wati_messages/fetch_messages",
                "post"
            );
            if (response.status) {
                setChatlist(response.data.final_data);
                setContinuationToken(response.data.continuationToken);
            }
        } catch (_) {
            // silent — WhatsappMessages shows error toasts for user-triggered reloads
        } finally {
            setChatlistLoading(false);
        }
    };

    // Load chatlist globally on app start
    useEffect(() => {
        getAllChats();
    }, []);

    // Process incoming socket events — chatlist upsert only
    useEffect(() => {
        if (watiChatUpdates.length <= watiProcessedCountRef.current) return;

        const newUpdates = watiChatUpdates.slice(watiProcessedCountRef.current);
        watiProcessedCountRef.current = watiChatUpdates.length;

        newUpdates.forEach(update => {
            if (
                update.target === "sendTextMessage" &&
                update["0"]?.message?.conViewModel?.channelId === CHANNEL_ID
            ) {
                const conv = update["0"].message.conViewModel;
                const isOpen = activeChatIdRef.current === update["0"].conversationId;

                const updatedEntry = {
                    id: conv.id,
                    name: conv.name,
                    lastUpdated: conv.lastUpdated,
                    lastMessageDate: conv.lastMessageDate,
                    lastIncomingDate: conv.lastIncomingDate,
                    type: conv.type,
                    text: conv.text,
                    lastMessageType: update["0"].messageType,
                    unreadCount: isOpen ? 0 : conv.unreadCount,
                    isStar: conv.isStar,
                    isBlocked: conv.isBlocked,
                    whatsappId: conv.whatsappId,
                    contactId: conv.contactId,
                    activeTicketType: conv.activeTicketType,
                    ticket: conv.ticket,
                    replyDeadline: conv.replyDeadline,
                    channelName: conv.channelName,
                };

                setChatlist(prev => {
                    const exists = prev.some(c => c.id === updatedEntry.id);
                    const next = exists
                        ? prev.map(c => c.id === updatedEntry.id ? updatedEntry : c)
                        : [...prev, updatedEntry];
                    return [...next].sort((a, b) => parseFloat(b.lastUpdated) - parseFloat(a.lastUpdated));
                });

                if (update["0"].message.isOwner === false) {
                    notificationAudioRef.current.currentTime = 0;
                    notificationAudioRef.current.play().catch(() => { });

                    if (location.pathname !== "/whatsapp_messages") {
                        const nKey = `wati-notif-${Date.now()}`;
                        const userName = authState?.user?.name;
                        const showCloseAll = activeNotifsRef.current.size >= 1;
                        const onCloseAll = () => {
                            activeNotifsRef.current.forEach(k => notification.destroy(k));
                            activeNotifsRef.current.clear();
                        };
                        activeNotifsRef.current.add(nKey);
                        notification.info({
                            key: nKey,
                            message: conv.name,
                            description: (
                                <>
                                    <div style={{ fontSize: 13, color: "#555", marginBottom: 2 }}>
                                        💬 {conv.text || "(media)"}
                                    </div>
                                    <QuickReplyBox
                                        phone={conv.whatsappId}
                                        userName={userName}
                                        notificationKey={nKey}
                                        showCloseAll={showCloseAll}
                                        onCloseAll={onCloseAll}
                                        onNavigate={() => {
                                            notification.destroy(nKey);
                                            navigate("/whatsapp_messages");
                                        }}
                                    />
                                </>
                            ),
                            placement: "bottomLeft",
                            duration: 0,
                            icon: <img src={whatsappIcon} style={{ width: 24, height: 24 }} alt="WhatsApp" />,
                            style: { width: 340 },
                            onClose: () => activeNotifsRef.current.delete(nKey),
                        });
                    }
                }
            }
        });
    }, [watiChatUpdates]);

    const totalUnread = chatlist.filter(c => c.unreadCount > 0).length;

    return (
        <WatiChatContext.Provider value={{
            chatlist,
            setChatlist,
            chatlistLoading,
            setChatlistLoading,
            continuationToken,
            setContinuationToken,
            getAllChats,
            activeChatIdRef,
            totalUnread,
        }}>
            {children}
        </WatiChatContext.Provider>
    );
};
